import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import MobileMenu from './navbar/MobileMenu';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Categories', path: '/categories' },
  { name: 'Services', path: '/services' },
  { name: 'How It Works', path: '/how-it-works' },
  { name: 'For Buyers', path: '/for-buyers' },
  { name: 'For Suppliers', path: '/for-suppliers' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // Intentionally runs once on mount only — setIsScrolled is a stable setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, setIsMobileMenuOpen]);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-[#E5E0D8]'
          : 'bg-white/80 backdrop-blur-sm'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src={`${process.env.PUBLIC_URL}/logo-navy.png`}
              alt="Taiga Sourcing"
              className="h-14 w-auto object-contain"
              data-testid="logo-image"
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
              }}
            />
            <span
              className="text-2xl font-bold tracking-tight"
              style={{ color: '#0B2A57', fontFamily: 'Cabinet Grotesk', display: 'none' }}
            >
              TAIGA SOURCING
            </span>
          </Link>

          <div className="hidden xl:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[13px] font-medium tracking-wide uppercase transition-colors hover:text-[#1E3D66] relative ${
                  location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path))
                    ? 'text-[#0B2A57]'
                    : 'text-[#6B7280]'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.name}
                {(location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path))) && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#0B2A57]"
                  />
                )}
              </Link>
            ))}
          </div>

          <Link
            to="/request-sourcing"
            className="hidden xl:block px-6 py-2.5 text-[13px] font-semibold tracking-wider uppercase text-white transition-all hover:shadow-lg hover:-translate-y-0.5"
            style={{ backgroundColor: '#0B2A57' }}
            data-testid="cta-button-header"
          >
            Request Sourcing
          </Link>

          <div className="flex xl:hidden items-center gap-3">
            <Link
              to="/request-sourcing"
              className="px-4 py-2 text-xs font-semibold tracking-wider uppercase text-white"
              style={{ backgroundColor: '#0B2A57' }}
              data-testid="cta-button-mobile-header"
            >
              Request Sourcing
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-[#0B2A57]"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      <MobileMenu isOpen={isMobileMenuOpen} navLinks={navLinks} currentPath={location.pathname} />
    </motion.nav>
  );
};

export default Navbar;
